import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { usePlayingBands } from "../library/usePlayingBands.js";
import { useBandStore } from "../../stores/bandStore.js";

function navClass({ isActive }: { isActive: boolean }): string {
  return [
    "font-mono text-[11px] uppercase tracking-widest2 px-3 py-1.5 border-b-2 transition-colors",
    isActive
      ? "text-paper border-vinyl"
      : "text-ink-muted border-transparent hover:text-paper hover:border-border",
  ].join(" ");
}

function HeaderMarquee(): JSX.Element | null {
  const playing = usePlayingBands();
  const bands = useBandStore((s) => s.bands);

  useEffect(() => {
    if (bands.length === 0) void useBandStore.getState().fetch();
  }, [bands.length]);

  if (playing.size === 0) return null;

  const tracks = bands.filter((b) => playing.has(b.id)).map((b) => b.name);
  if (tracks.length === 0) return null;
  const repeated = [...tracks, ...tracks, ...tracks, ...tracks];

  return (
    <div className="overflow-hidden border-b border-border bg-tape">
      <div className="marquee-track py-1.5">
        {repeated.map((name, i) => (
          <span
            key={`${name}-${String(i)}`}
            className="flex shrink-0 items-center gap-3 font-mono text-[10px] uppercase tracking-widest2 text-butter"
          >
            <span className="inline-block h-1.5 w-1.5 animate-pulse bg-vinyl" />
            On Air · {name}
            <span className="text-ink-dim">— gerando faixa —</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Brand(): JSX.Element {
  return (
    <div className="flex items-baseline gap-3">
      <div className="relative">
        {/* Spinning vinyl badge */}
        <svg width="32" height="32" viewBox="0 0 32 32" className="vinyl-spinning">
          <circle cx="16" cy="16" r="15" fill="#0d0a06" stroke="#3a2f24" />
          <circle cx="16" cy="16" r="11" fill="none" stroke="#3a2f24" strokeWidth="0.5" />
          <circle cx="16" cy="16" r="8" fill="none" stroke="#3a2f24" strokeWidth="0.5" />
          <circle cx="16" cy="16" r="5" fill="#d6402e" />
          <circle cx="16" cy="16" r="1.5" fill="#0d0a06" />
        </svg>
      </div>
      <div className="flex flex-col leading-none">
        <span
          className="font-display text-[22px] font-medium tracking-tight text-paper"
          style={{ fontVariationSettings: '"opsz" 96, "wght" 700, "SOFT" 100, "WONK" 1' }}
        >
          Studio<span className="text-vinyl">.</span>
        </span>
        <span className="catalog-num mt-1">MUSIC LAB / SIDE B</span>
      </div>
    </div>
  );
}

export function AppShell(): JSX.Element {
  const [year] = useState(() => new Date().getFullYear());
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-border bg-asphalt/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-col items-stretch gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 md:px-6">
          <NavLink to="/bands" className="self-start sm:self-auto">
            <Brand />
          </NavLink>
          <nav className="flex items-center gap-1 overflow-x-auto">
            <NavLink to="/bands" className={navClass}>
              Bandas
            </NavLink>
            <NavLink to="/artists" className={navClass}>
              Artistas
            </NavLink>
            <NavLink to="/library" className={navClass}>
              Catálogo
            </NavLink>
          </nav>
        </div>
        <HeaderMarquee />
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">
        <Outlet />
      </main>
      <footer className="mx-auto mt-16 max-w-6xl border-t border-border px-4 py-6 md:px-6">
        <div className="flex flex-col items-baseline justify-between gap-1 font-mono text-[10px] uppercase tracking-widest2 text-ink-dim sm:flex-row">
          <span>Studio Records · est. {year}</span>
          <span>side b / lo-fi pressing</span>
        </div>
      </footer>
    </div>
  );
}
